import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/types';
import type { ComicItem } from '@/mocks/types';
import ComicCard from '@/pages/home/components/ComicCard';

const categoryPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '动漫之家漫画分类 - 韩国漫画在线浏览',
  description: '浏览动漫之家全部分类韩国漫画，恋爱、奇幻、动作、悬疑、穿越等多种类型，高清无删减下拉式阅读。',
  url: 'https://dongmanzhijia.com/category',
  isPartOf: {
    '@type': 'WebSite',
    name: '动漫之家',
    url: 'https://dongmanzhijia.com',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const sortParam = searchParams.get('sort') || 'newest';
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>(sortParam);

  useEffect(() => {
    if (slug) {
      const cat = categories.find((c) => c.slug === slug);
      if (cat) {
        setActiveCategory(cat.id);
      }
    } else {
      setActiveCategory('all');
    }
  }, [slug]);

  const activeCategoryName = useMemo(() => {
    if (activeCategory === 'all') return '全部漫画';
    const cat = categories.find((c) => c.id === activeCategory);
    return cat ? cat.name : '全部漫画';
  }, [activeCategory]);

  const filteredComics = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...comics]
      : comics.filter((c) => c.categoryId === activeCategory);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const sortLabel = sortBy === 'rating' ? '评分最高' : sortBy === 'popular' ? '人气最高' : '最新更新';
  const pageTitle = activeCategory === 'all' ? '全部分类' : activeCategoryName;

  return (
    <>
      <SEOHead
        title={`${pageTitle}漫画 - 韩国漫画在线浏览`}
        description={`浏览动漫之家${pageTitle}韩国漫画，高清无删减下拉式阅读体验，每日更新，全部免费。涵盖恋爱、奇幻、动作、悬疑等丰富类型。`}
        keywords={`${pageTitle},韩国漫画,无删减漫画,下拉式漫画,韩漫在线免费阅读,动漫之家`}
        canonicalUrl={`https://dongmanzhijia.com/category${slug ? `/${slug}` : ''}`}
        schemaData={categoryPageSchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '漫画分类' },
                ]}
              />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {activeCategory === 'all' ? '全部分类' : activeCategoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部作品 · {sortLabel}
              </p>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === 'all'
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex bg-background-100 rounded-full p-1">
                {[
                  { value: 'newest', label: '最新' },
                  { value: 'rating', label: '评分' },
                  { value: 'popular', label: '人气' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-book-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <div key={comic.id} className="flex justify-center">
                    <ComicCard comic={comic} size="medium" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}